/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PrescriptionTemplateTable from "components/tables/PrescriptiontTemplateTable";
import useHttp from "hooks/http.hook";
import { FC, useEffect, useState } from "react";
import { TemplateInterface, QueryResultInterface, TemplateTypeEnum } from "@malmur-technologies/medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import { useTranslation } from "react-i18next";
import { TagsInterface } from "interfaces";
import useSort from "hooks/useSort.hook";

/**
 * Props for the PrescriptionTemplatesDataContent component.
 * @interface PrescriptionTemplatesDataContentProps
 * @property {(selectedPrescription: TemplateInterface) => void} [selectFunction] - Callback when a prescription template is selected
 * @property {boolean} [readOnly] - Whether the table should be read-only
 * @property {string} [customTag] - Custom tag filter for prescription templates
 * @property {TagsInterface[]} [filterTag] - Array of tags to filter templates by
 */
interface PrescriptionTemplatesDataContentProps {
  selectFunction?: (selectedPrescription: TemplateInterface) => void;
  readOnly?: boolean;
  customTag?: string;
  filterTag?: TagsInterface[];
}

/**
 * Content component for displaying and managing prescription templates.
 * Shows a table of prescription templates that can be filtered by tags and selected
 * for use in creating patient prescriptions.
 * @param {PrescriptionTemplatesDataContentProps} props - Component props
 * @returns {React.ReactElement} The prescription templates content component
 */
const PrescriptionTemplatesDataContent: FC<PrescriptionTemplatesDataContentProps> = ({
  selectFunction,
  readOnly,
  filterTag,
  customTag,
}) => {
  const [,,sendRequest] = useHttp();
  const [prescriptionTemplateList, setPrescriptionTemplateList] = useState<TemplateInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [templateFilter, setTemplateFilter] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateInterface | null>(null);
  const { t } = useTranslation();
  const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();

  useEffect(() => {
    fetchPrescriptionTemplates();
  }, [page, pageSize, templateFilter, filterTag, buildSortParam]);

  const fetchPrescriptionTemplates = () => {
    const titleParam = templateFilter ? `title=${templateFilter}&` : "";
    const url = `${CoreAPI_URLs.PRESCRIPTION_TEMPLATE_GET_ALL}?${buildSortParam()}type=${TemplateTypeEnum.PRESCRIPTION}&customTag=${customTag}&${titleParam}page=${page}&pageSize=${pageSize}`;
    sendRequest(url, onGetPrescriptionTemplateHandler, "GET");
  };

  const onGetPrescriptionTemplateHandler = (
    prescriptionTemplatesResponse: QueryResultInterface<TemplateInterface>
  ) => {
    setPrescriptionTemplateList(prescriptionTemplatesResponse.results);
    setRowsTotal(prescriptionTemplatesResponse.total);
    setPageTotal(Math.ceil(prescriptionTemplatesResponse.total / pageSize));
  };

  const handleSelect = (template: TemplateInterface) => {
    setSelectedTemplate(template);
    if (selectFunction) {
      selectFunction(template);
    }
  };
  return (
    <Box>
      <PrescriptionTemplateTable
        modelList={prescriptionTemplateList}
        setModelList={setPrescriptionTemplateList}
        canSearchPlaceholder={t("prescriptiontemplatesDataContent.searchPrescriptionTemplate")}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        selectFunction={handleSelect}
        readOnlyValue={readOnly}
        setModelFilter={setTemplateFilter}
        selectedRow={selectedTemplate}
        customTag={customTag}
        filterTag={filterTag}
        onSortChange={handleSortChange}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
      />
    </Box>
  );
};

export default PrescriptionTemplatesDataContent;
